import { useShallow } from "zustand/react/shallow";
import { useInsightsStore } from "../stores/insightsStore";
import type { TagRecommendation, WorkflowExample } from "../types/insights";

/**
 * Hook for accessing customer insights data and actions.
 * Provides personalized recommendations based on wallet activity and company context.
 *
 * @example
 * ```tsx
 * const { insights, isLoading, chainSuggestions, startEnrichment } = useInsights();
 *
 * // Start enrichment during onboarding
 * await startEnrichment({ walletAddress, email, userId });
 *
 * // Access personalized data
 * const chains = chainSuggestions; // [1, 8453, 42161]
 * const examples = workflowExamples; // [{prompt: "Alert when ETH drops 5%", ...}]
 * ```
 */
export function useInsights() {
  return useInsightsStore(
    useShallow(state => ({
      // Data
      insights: state.data,
      enrichmentId: state.enrichmentId,
      status: state.status,
      error: state.error,
      lastEnrichedAt: state.lastEnrichedAt,

      // Loading state
      isLoading: state.isLoading(),

      // Convenience selectors (cached arrays — referentially stable)
      chainSuggestions: state.chainSuggestions,
      tagRecommendations: state.tagRecommendations,
      workflowExamples: state.workflowExamples,

      // Actions
      startEnrichment: state.startEnrichment,
      reset: state.reset,
    })),
  );
}

/**
 * Hook for accessing just the chain suggestions.
 * Useful for ChainSelector/MultiChainSelector components.
 */
export function useChainSuggestions(): number[] {
  return useInsightsStore(state => state.chainSuggestions);
}

/**
 * Hook for accessing just the tag recommendations.
 * Useful for tag-related components.
 */
export function useTagRecommendations(): TagRecommendation[] {
  return useInsightsStore(state => state.tagRecommendations);
}

/**
 * Hook for accessing just the workflow examples.
 * Useful for AI input placeholder suggestions.
 */
export function useWorkflowExamples(): WorkflowExample[] {
  return useInsightsStore(state => state.workflowExamples);
}

/**
 * Hook for the personalized welcome message.
 */
export function usePersonalizedMessage(): string | null {
  return useInsightsStore(state => state.data?.recommendations?.personalizedMessage ?? null);
}
